import React, { useState, useEffect, useContext } from 'react';
import {
  Button, Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel,
  TextField, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { getPriceTierBands, setPriceTierBands } from '../../Actions';
import { AlertContext } from '../../Context/AlertContextProvider';
import PriceTierBadge from '../Product/V2/PriceTierBadge';

const SWATCH_SIZE = 28;

// Mirrors the QR price-rule UOM list — the band semantics are the same, so the
// choices a user sees should be too.
const UOM_OPTIONS = [
  { value: '', label: 'SF / SY (sq ft)' },
  { value: 'LF', label: 'LF — Linear Foot' },
  { value: 'LY', label: 'LY — Linear Yard' },
  { value: 'EA', label: 'EA — Each / Piece' },
  { value: 'PC', label: 'PC — Piece' },
  { value: 'BX', label: 'BX — Box' },
  { value: 'SY', label: 'SY — Sq Yard (raw)' },
  { value: 'SF', label: 'SF — Sq Foot (raw)' },
];

const BLANK_BAND = {
  band_name: '',
  band_label: '',
  min_price_sqft: '',
  max_price_sqft: '',
  uom_type_cd: '',
  fg_color: '#FFFFFF',
  bg_color: '#2E7D32',
};

const ColorCell = ({ value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: SWATCH_SIZE, height: SWATCH_SIZE, cursor: 'pointer', border: 'none', padding: 0, borderRadius: 4 }}
    />
    <Typography variant="caption" style={{ fontFamily: 'monospace', fontSize: 11, marginLeft: 6 }}>{value}</Typography>
  </div>
);

// '' from the form means "unbounded"; the API wants null.
const toApi = (b) => ({
  band_name: b.band_name,
  band_label: b.band_label,
  min_price_sqft: b.min_price_sqft !== '' ? parseFloat(b.min_price_sqft) : null,
  max_price_sqft: b.max_price_sqft !== '' ? parseFloat(b.max_price_sqft) : null,
  uom_type_cd: b.uom_type_cd || null,
  fg_color: b.fg_color,
  bg_color: b.bg_color,
});

const fromApi = (b) => ({
  band_name: b.band_name ?? '',
  band_label: b.band_label ?? '',
  min_price_sqft: b.min_price_sqft != null ? String(parseFloat(b.min_price_sqft)) : '',
  max_price_sqft: b.max_price_sqft != null ? String(parseFloat(b.max_price_sqft)) : '',
  uom_type_cd: b.uom_type_cd ?? '',
  fg_color: b.fg_color ?? '#FFFFFF',
  bg_color: b.bg_color ?? '#000000',
});

// Catches the mistakes that would silently mis-band a product, before saving.
// The server validates the same things — this is just a faster, clearer loop.
const validate = (bands) => {
  for (const b of bands) {
    if (!b.band_label.trim()) {
      return 'Every band needs a label — it is the only thing the customer sees.';
    }
    if (b.min_price_sqft === '' && b.max_price_sqft === '') {
      return `Band "${b.band_label}" needs a minimum or a maximum price.`;
    }
    if (
      b.min_price_sqft !== '' && b.max_price_sqft !== '' &&
      parseFloat(b.min_price_sqft) >= parseFloat(b.max_price_sqft)
    ) {
      return `Band "${b.band_label}" has a minimum at or above its maximum.`;
    }
  }

  // Overlaps are legal (first match by order wins) but almost never intended, so
  // warn by naming the pair rather than blocking the save.
  const sqft = bands.filter((b) => !b.uom_type_cd);
  for (let i = 0; i < sqft.length; i++) {
    for (let j = i + 1; j < sqft.length; j++) {
      const a = sqft[i];
      const c = sqft[j];
      const aMin = a.min_price_sqft === '' ? -Infinity : parseFloat(a.min_price_sqft);
      const aMax = a.max_price_sqft === '' ? Infinity : parseFloat(a.max_price_sqft);
      const cMin = c.min_price_sqft === '' ? -Infinity : parseFloat(c.min_price_sqft);
      const cMax = c.max_price_sqft === '' ? Infinity : parseFloat(c.max_price_sqft);
      if (aMin < cMax && cMin < aMax) {
        return `Bands "${a.band_label}" and "${c.band_label}" overlap — the higher one in the list will always win.`;
      }
    }
  }

  return null;
};

// Admin editor for the price bands that stand in for prices on the customer-facing
// product page when PRICE_TIER_MODE is on. Bands are per location, with a default
// set used by any store that has none of its own.
const PriceTierBands = () => {
  const { handleAlertOpen, setMessageType, setMessage } = useContext(AlertContext);

  const [scope, setScope] = useState('');          // '' = instance-wide default set
  const [scopes, setScopes] = useState([]);        // locations + their band counts
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const alert = (type, text) => {
    setMessageType(type);
    setMessage(text);
    handleAlertOpen();
  };

  const load = (locationId) => {
    setLoading(true);
    getPriceTierBands(locationId)
      .then((res) => {
        const data = res.data?.data ?? {};
        setBands((data.bands ?? []).map(fromApi));
        setScopes(data.scopes ?? []);
      })
      .catch(() => alert('error', 'Could not load price tier bands'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(scope); }, [scope]);

  const updateBand = (i, field, value) => {
    setBands((prev) => prev.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)));
  };

  const addBand = () => setBands((prev) => [...prev, { ...BLANK_BAND }]);

  const removeBand = (i) => setBands((prev) => prev.filter((_, idx) => idx !== i));

  const save = () => {
    const problem = validate(bands);
    if (problem) {
      alert('error', problem);
      return;
    }

    setSaving(true);
    setPriceTierBands(scope === '' ? null : scope, bands.map(toApi))
      .then(() => {
        alert('success', 'Price tier bands saved');
        load(scope);
      })
      .catch((err) => alert('error', err?.response?.data?.message ?? 'Could not save price tier bands'))
      .finally(() => setSaving(false));
  };

  const scopeLabel = scope === ''
    ? 'the default set, used by any store without its own bands'
    : `${scopes.find((s) => String(s.location_id) === String(scope))?.location_name ?? 'this store'} only`;

  return (
    <div style={{ padding: '10px 0' }}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
        When price tier mode is on, customers scanning a QR code see one of these
        bands instead of a price. Staff can then tell a customer which bands are
        within their budget without naming figures. Bands are matched in list
        order — the first one a price falls into wins.
      </Typography>

      <FormControl style={{ minWidth: 280, marginBottom: 18 }}>
        <InputLabel shrink>Bands for</InputLabel>
        <Select value={scope} onChange={(e) => setScope(e.target.value)} displayEmpty>
          <MenuItem value="">Default (all stores)</MenuItem>
          {scopes.map((s) => (
            <MenuItem key={s.location_id} value={s.location_id}>
              {s.location_name}
              {s.band_count > 0 ? ` — ${s.band_count} own band${s.band_count === 1 ? '' : 's'}` : ' — uses default'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
        Editing {scopeLabel}.
        {scope !== '' ? ' Saving any band here stops this store using the default set.' : ''}
      </Typography>

      <Divider style={{ marginBottom: 12 }} />

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Preview</TableCell>
                  <TableCell>Name (internal)</TableCell>
                  <TableCell>Label (customer sees)</TableCell>
                  <TableCell>UOM</TableCell>
                  <TableCell>Min price</TableCell>
                  <TableCell>Max price</TableCell>
                  <TableCell>Text</TableCell>
                  <TableCell>Background</TableCell>
                  <TableCell style={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {bands.map((band, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <PriceTierBadge
                        tier={{ label: band.band_label || '?', fg_color: band.fg_color, bg_color: band.bg_color }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={band.band_name}
                        onChange={(e) => updateBand(i, 'band_name', e.target.value)}
                        placeholder="e.g. Budget"
                        size="small"
                        inputProps={{ style: { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={band.band_label}
                        onChange={(e) => updateBand(i, 'band_label', e.target.value)}
                        placeholder="e.g. A"
                        size="small"
                        inputProps={{ maxLength: 40, style: { fontSize: 13, width: 90 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={band.uom_type_cd}
                        onChange={(e) => updateBand(i, 'uom_type_cd', e.target.value)}
                        displayEmpty
                        style={{ fontSize: 13, minWidth: 140 }}
                      >
                        {UOM_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value} style={{ fontSize: 13 }}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={band.min_price_sqft}
                        onChange={(e) => updateBand(i, 'min_price_sqft', e.target.value)}
                        placeholder="0"
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01, style: { fontSize: 13, width: 72 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={band.max_price_sqft}
                        onChange={(e) => updateBand(i, 'max_price_sqft', e.target.value)}
                        placeholder="∞"
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01, style: { fontSize: 13, width: 72 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <ColorCell value={band.fg_color} onChange={(v) => updateBand(i, 'fg_color', v)} />
                    </TableCell>
                    <TableCell>
                      <ColorCell value={band.bg_color} onChange={(v) => updateBand(i, 'bg_color', v)} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeBand(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {bands.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="caption" color="textSecondary">
                        No bands{scope !== '' ? ' for this store — it uses the default set' : ''}. With price
                        tier mode on and no bands, customers see "Ask our staff" instead of a badge.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div style={{ marginTop: 16 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addBand}
              style={{ borderColor: '#4E564E', color: '#4E564E' }}
            >
              Add Band
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={save}
              disabled={saving}
              style={{ marginLeft: 10, backgroundColor: '#4E564E', color: '#FFFFFF' }}
            >
              {saving ? 'Saving…' : 'Save Bands'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PriceTierBands;
